// SnowTrack Feature Flags - Gate features by subscription tier

// Tier hierarchy (higher number = more features)
const TIER_LEVELS = {
  free: 0,
  basic: 1,
  pro: 2,
  premium: 3,
  enterprise: 4,
};

// Feature to minimum tier mapping
const FEATURE_TIERS = {
  // Pro tier features
  expense_tracking: 'pro',
  reports: 'pro',
  square_integration: 'pro',
  // Text messaging (SMS Phase 2). Mirrors sms in server/config/plans.js.
  sms: 'pro',

  // On every plan (audit C5 / Finding 2.8d). These were mapped to 'premium',
  // but no requireTier guards them on the server and no FeatureGate wraps
  // them in the client, so a Basic company can already use both. The pricing
  // page lists them on every plan and this map now agrees with it.
  customer_portal: 'free',
  multi_user: 'free',

  // Premium tier features. Not built and not advertised anywhere; kept only
  // because FeatureGate's JSDoc references it. No call site passes it.
  advanced_reports: 'premium',

  // Basic/Free features (everyone gets these)
  customers: 'free',
  routes: 'free',
  storm_mode: 'free',
  weather: 'free',
  intake_form: 'free',
  discounts: 'free',
  multiple_properties: 'free',
};

// Human-readable feature names for UI
const FEATURE_NAMES = {
  expense_tracking: 'Expense Tracking',
  reports: 'Profit & Loss Reports',
  square_integration: 'Square Integration',
  sms: 'Text Messaging',
  customer_portal: 'Customer Portal',
  multi_user: 'Multi-User Access',
  advanced_reports: 'Advanced Reports',
  customers: 'Customer Management',
  routes: 'Route Management',
  storm_mode: 'Storm Mode',
  weather: 'Weather Forecasts',
  intake_form: 'Customer Intake Form',
  discounts: 'Referral Discounts',
  multiple_properties: 'Multiple Properties',
};

// Human-readable tier names
const TIER_NAMES = {
  free: 'Free',
  basic: 'Basic',
  pro: 'Pro',
  premium: 'Premium',
  enterprise: 'Enterprise',
};

/**
 * What a subscription tier is actually worth right now.
 *
 * subscription_tier records what the company BOUGHT; subscription_status says
 * whether they still have it. Reading the tier alone leaves a lapsed account
 * with Pro features forever, so every access check goes through here first.
 *
 *   active / trialing  - paid up, full access
 *   trial              - SnowTrack's own trial, which runs at Pro; the storm
 *                        event limit is what ends it, not this function
 *   past_due           - full access. Stripe is still retrying the charge and
 *                        locking someone out mid-storm over a card that will
 *                        probably clear tomorrow is worse than a few free days
 *   cancelled          - full access until the period they paid for ends,
 *                        then free
 *   expired            - free
 *
 * Anything else keeps its tier. Statuses we do not recognise are almost
 * always legacy rows or a value written before this map existed, and
 * downgrading a paying customer on a string we failed to anticipate is a far
 * worse failure than briefly honouring one we should not have.
 *
 * @param {string} tier - subscription_tier from the company row
 * @param {string} status - subscription_status from the company row
 * @param {string|Date} currentPeriodEnd - subscription_current_period_end
 * @returns {string} - The tier to gate features against
 */
export const resolveEffectiveTier = (tier, status, currentPeriodEnd) => {
  const resolvedTier = String(tier || 'free').toLowerCase();
  const resolvedStatus = String(status || '').toLowerCase();

  if (resolvedStatus === 'expired') return 'free';

  // Stripe spells it 'canceled'; our own columns have used 'cancelled'.
  if (resolvedStatus === 'cancelled' || resolvedStatus === 'canceled') {
    if (!currentPeriodEnd) return 'free';
    const endsAt = new Date(currentPeriodEnd);
    // An unparseable date is not evidence the plan has lapsed.
    if (Number.isNaN(endsAt.getTime())) return resolvedTier;
    return endsAt.getTime() > Date.now() ? resolvedTier : 'free';
  }

  return resolvedTier;
};

/**
 * Check if a user's tier has access to a feature
 * @param {string} userTier - The user's subscription tier (free, basic, pro, premium)
 * @param {string} featureKey - The feature key to check
 * @returns {boolean} - True if user has access, false otherwise
 */
export const hasFeature = (userTier, featureKey) => {
  const requiredTier = FEATURE_TIERS[featureKey];

  // If feature doesn't exist in our map, deny access by default
  if (!requiredTier) {
    console.warn(`Unknown feature: ${featureKey}`);
    return false;
  }

  const userLevel = TIER_LEVELS[userTier] ?? 0;
  const requiredLevel = TIER_LEVELS[requiredTier] ?? 0;

  return userLevel >= requiredLevel;
};

/**
 * Get the required tier for a feature
 * @param {string} featureKey - The feature key
 * @returns {string} - The tier name required for this feature
 */
export const getRequiredTier = (featureKey) => {
  return FEATURE_TIERS[featureKey] || 'premium';
};

/**
 * Get the human-readable name of a feature
 * @param {string} featureKey - The feature key
 * @returns {string} - Human-readable feature name
 */
export const getFeatureName = (featureKey) => {
  return FEATURE_NAMES[featureKey] || featureKey;
};

/**
 * Get the human-readable name of a tier
 * @param {string} tierKey - The tier key
 * @returns {string} - Human-readable tier name
 */
export const getTierName = (tierKey) => {
  return TIER_NAMES[tierKey] || tierKey;
};

/**
 * Get all features available at a specific tier
 * @param {string} tierKey - The tier to check
 * @returns {string[]} - Array of feature keys available at this tier
 */
export const getFeaturesForTier = (tierKey) => {
  const tierLevel = TIER_LEVELS[tierKey] ?? 0;

  return Object.entries(FEATURE_TIERS)
    .filter(([, requiredTier]) => TIER_LEVELS[requiredTier] <= tierLevel)
    .map(([feature]) => feature);
};

export { FEATURE_TIERS, TIER_LEVELS, FEATURE_NAMES, TIER_NAMES };
